/**
 * House operating timezone — all window/cutoff clocks use Cuba civil time.
 * Cuba is UTC-5 year-round (no DST since 2015).
 *
 * We use a fixed UTC-5 offset rather than `Intl`/`America/Havana` wall parts:
 * some ICU builds still emit a summer GMT-4 offset for Havana, which would
 * shift every acceptance window by one hour.
 */
export const HOUSE_TIMEZONE = 'America/Havana';

/** Hours to add to Cuba wall clock to reach UTC (Cuba = UTC−5). */
const CUBA_UTC_OFFSET_HOURS = 5;
const CUBA_OFFSET_MS = CUBA_UTC_OFFSET_HOURS * 3_600_000;

export interface CubaTimeParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/** UTC instant → Cuba civil parts via fixed UTC−5. */
export function getCubaTimeParts(utcInstant: Date): CubaTimeParts {
  const civil = new Date(utcInstant.getTime() - CUBA_OFFSET_MS);
  return {
    year: civil.getUTCFullYear(),
    month: civil.getUTCMonth() + 1,
    day: civil.getUTCDate(),
    hours: civil.getUTCHours(),
    minutes: civil.getUTCMinutes(),
    seconds: civil.getUTCSeconds(),
  };
}

export function cubaMinutesOfDay(utcInstant: Date): number {
  const p = getCubaTimeParts(utcInstant);
  return p.hours * 60 + p.minutes;
}

export function cubaDayKey(utcInstant: Date): string {
  const p = getCubaTimeParts(utcInstant);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Monday = 0 … Sunday = 6 for the Cuba civil calendar date. */
function cubaWeekdayMon0(utcInstant: Date): number {
  const p = getCubaTimeParts(utcInstant);
  const civilUtc = Date.UTC(p.year, p.month - 1, p.day);
  // getUTCDay: Sun=0 … Sat=6 → Mon=0 … Sun=6
  return (new Date(civilUtc).getUTCDay() + 6) % 7;
}

/** Monday-start week boundary as Cuba `YYYY-MM-DD`. */
export function cubaWeekStartDayKey(utcInstant: Date): string {
  const p = getCubaTimeParts(utcInstant);
  const start = addCubaCalendarDays(p.year, p.month, p.day, -cubaWeekdayMon0(utcInstant));
  return `${start.year}-${String(start.month).padStart(2, '0')}-${String(start.day).padStart(2, '0')}`;
}

/** Cuba wall-clock → UTC instant (fixed UTC−5). */
export function cubaWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
): Date {
  return new Date(Date.UTC(year, month - 1, day, hour + CUBA_UTC_OFFSET_HOURS, minute, second));
}

export function addCubaCalendarDays(
  year: number,
  month: number,
  day: number,
  days: number,
): Pick<CubaTimeParts, 'year' | 'month' | 'day'> {
  const anchor = cubaWallTimeToUtc(year, month, day, 12, 0, 0);
  const shifted = new Date(anchor.getTime() + days * 86_400_000);
  const p = getCubaTimeParts(shifted);
  return { year: p.year, month: p.month, day: p.day };
}

export function parseTimeToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Build a UTC Date whose UTC fields equal Cuba civil parts.
 * Pair with `timeZone: 'UTC'` in Intl so summer ICU Havana DST cannot skew labels.
 */
function cubaPartsAsUtcDate(p: CubaTimeParts): Date {
  return new Date(Date.UTC(p.year, p.month - 1, p.day, p.hours, p.minutes, p.seconds));
}

/** Status-bar style `date · time` in house civil time (fixed UTC−5). */
export function formatCubaDateTimeLabel(utcInstant: Date, locale?: string): string {
  const asUtc = cubaPartsAsUtcDate(getCubaTimeParts(utcInstant));
  const date = asUtc.toLocaleDateString(locale, { timeZone: 'UTC' });
  const time = asUtc.toLocaleTimeString(locale, {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${date} · ${time}`;
}

/** Short time label in house civil time (fixed UTC−5). */
export function formatCubaTimeLabel(utcInstant: Date, locale?: string): string {
  const asUtc = cubaPartsAsUtcDate(getCubaTimeParts(utcInstant));
  return asUtc.toLocaleTimeString(locale, {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `YYYY-MM-DD` → short date label without browser-local day shifts. */
export function formatCubaDayKeyLabel(dayKey: string, locale = 'es-CU'): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  if (!y || !m || !d) return dayKey;
  try {
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(locale, {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return dayKey;
  }
}
